import { useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { EntryJourneyStepper } from '@/components/entry-journeys';
import { OnboardingShell } from '@/components/onboarding/OnboardingShell';
import { PROFILE_FORM_ID, ProfileStep } from '@/components/onboarding/ProfileStep';
import { StepFooter } from '@/components/onboarding/StepFooter';
import { readEntryJourney } from '@/lib/entry-journey';
import { readPendingInviteToken } from '@/components/onboarding/pending-invite';
import { useMe } from '@/lib/queries/identity';

export const Route = createFileRoute('/onboarding/profile')({
  // A journey user bounced here by a generic gate (e.g. hitting an authed
  // URL mid-onboarding) belongs in the journey steps, not the organic
  // profile. Invites outrank journeys — their persona preset lives here.
  beforeLoad: () => {
    if (readEntryJourney() !== null && readPendingInviteToken() === null) {
      throw redirect({ to: '/onboarding/j/about' });
    }
  },
  component: OnboardingProfilePage,
});

function OnboardingProfilePage() {
  const { data, isError } = useMe();
  // Ad-journey funnel position: the profile step is "About you". Read once at
  // mount (established sessionStorage pattern); null for organic signups, so
  // the shell renders without the stepper slot, exactly as before.
  const [journey] = useState(readEntryJourney);
  // Mirror the old server page: swallow fetch errors and hand `undefined`
  // to ProfileStep so it falls back to defaults.
  //
  // `place` hydration: ProfileSelfResponse still exposes the legacy flat
  // `location` field via OpenAPI (server view migration is Wave 2). With no
  // placeId/lat/lng available, we can't hydrate a real `PlaceSelection` —
  // the picker starts empty and the user re-picks. The backfill agent's
  // migration covers existing DB rows server-side.
  const initialProfile =
    data !== undefined && !isError
      ? {
          firstName: data.firstName,
          lastName: data.lastName ?? '',
          jobTitle: data.jobTitle ?? '',
          industry: data.industry ?? '',
          place: null,
          displayName: [data.firstName, data.lastName].filter(Boolean).join(' '),
          ...(data.photoUrl !== undefined ? { photoUrl: data.photoUrl } : {}),
          photoOriginalUrl: data.photoOriginalUrl,
          photoCrop: data.photoCrop,
        }
      : undefined;

  return (
    <OnboardingShell
      title="Create your profile."
      {...(journey !== null
        ? {
            stepper: (
              <EntryJourneyStepper journey={journey} current="about-you" />
            ),
          }
        : {})}
      footer={
        <StepFooter formId={PROFILE_FORM_ID} primaryLabel="Continue" />
      }
    >
      <ProfileStep initialProfile={initialProfile} />
    </OnboardingShell>
  );
}
